// hooks/useRegistrationSubmit.js
import {useIntl, defineMessage} from 'react-intl'
import {AuthHelpers, useAuthHelper, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import usedDeviceDetection from './use-device-detection'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {sendViewErrorEvent, sendSignUpEventVerification} from '../utils/gtm-utils'
import {useAuthAuxHelper} from './use-auth-aux-helper'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useBrand, toBrandCode} from '../contexts/brand-context'
import {SITE_COUNTRY_MAP} from '../constants'
/**
 * Custom hook for handling registration submission logic
 *
 * @param {Object} form - React Hook Form instance
 * @param {Function} onSuccess - Callback function executed on successful registration
 * @param {Function} onAccountVerificationRequired - Callback when account verification is needed
 * @returns {Object} Hook methods
 */
const useRegistrationSubmit = (form, onSuccess, onAccountVerificationRequired) => {
    const {formatMessage} = useIntl()
    const register = useAuthHelper(AuthHelpers.Register)
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const config = getConfig()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const country = SITE_COUNTRY_MAP[siteId]
    const login = useAuthAuxHelper(AuthHelpers.LoginRegisteredUserB2C)
    const {executeRecaptcha} = useGoogleReCaptcha()
    const device = usedDeviceDetection()
    const navigate = useNavigation()
    const {brandId, isGarage} = useBrand()
    const brandCode = toBrandCode(brandId)

    /**
     * Process SFID registration
     * @private
     */
    const processSFIDRegistration = async (data) => {
        // Get reCAPTCHA token
        let recaptchaToken = null
        if (executeRecaptcha) {
            try {
                recaptchaToken = await executeRecaptcha('registration')
            } catch (error) {
                console.error('reCAPTCHA error:', error)
            }
        } else {
            console.warn('reCAPTCHA not available - proceeding without verification')
        }

        const token = await getTokenWhenReady()

        const response = await fetch(
            `${appOrigin}/process-register?siteId=${siteId}&locale=${encodeURIComponent(locale?.id || '')}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    token,
                    form: {
                        ...(recaptchaToken && {recaptchaToken}),
                        device: device,
                        emailAddress: data.email,
                        password: data.password,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        emailSubscribe: data.emailSubscribe ?? false,
                        birthday: data.birthDay,
                        phone: data.phone,
                        country,
                        brand: brandCode
                    }
                })
            }
        )

        if (!response.ok) {
            throw new Error('Failed to process registration')
        }

        const registrationData = await response.json()

        if (!registrationData?.success) {
            let errorMessage = ''
            let isDuplicateCustomer = false

            if (registrationData?.error === 'duplicate_customer') {
                isDuplicateCustomer = true
                errorMessage = isGarage
                    ? formatMessage(
                          defineMessage({
                              id: 'registration.error.duplicate_customer.garage',
                              defaultMessage:
                                  'This email address already exists. Please try again or <signIn>sign in</signIn>. If the issue persists, please contact <customerService>Customer Service</customerService>.'
                          })
                      )
                    : formatMessage(
                          defineMessage({
                              id: 'registration.error.duplicate_customer.dynamite',
                              defaultMessage:
                                  'This email address already exists. Please try again or <signIn>sign in</signIn>. If the issue persists, please contact <customerService>Customer Service</customerService>.'
                          })
                      )
            } else {
                errorMessage = formatMessage(API_ERROR_MESSAGE)
            }

            form.setError('global', {
                type: 'manual',
                message: errorMessage,
                isDuplicateCustomer
            })

            // GTM Modification
            sendViewErrorEvent(errorMessage)
            return null
        }

        return registrationData
    }

    /**
     * Process SFID login after registration
     * @private
     */
    const processSFIDLogin = async (email, password) => {
        // Get reCAPTCHA token
        let recaptchaToken = ''
        if (executeRecaptcha) {
            try {
                recaptchaToken = await executeRecaptcha('login')
            } catch (error) {
                console.error('reCAPTCHA error:', error)
            }
        } else {
            console.warn('reCAPTCHA not available - proceeding without verification')
        }

        const token = await getTokenWhenReady()

        const loginResponse = await fetch(`${appOrigin}/process-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                token,
                username: email,
                password: password,
                recaptchaToken
            })
        })

        if (!loginResponse.ok) {
            if (loginResponse.status === 403) {
                throw new Error('ACCOUNT_NOT_VERIFIED')
            }

            throw new Error('Failed to process login after registration')
        }

        const loginData = await loginResponse.json()

        if (!loginData?.password) {
            throw new Error('Invalid login data received')
        }

        return loginData
    }

    /**
     * Process Commerce API registration
     * @private
     */
    const processCommerceRegistration = async (data, loginData) => {
        const body = {
            customer: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                login: loginData.username
            },
            password: loginData.password
        }

        return await register.mutateAsync(body, {})
    }

    /**
     * Main form submission handler
     */
    const submitForm = async (data) => {
        try {
            const registrationData = await processSFIDRegistration(data)
            if (!registrationData) {
                return
            }

            const loginData = await processSFIDLogin(data.email, data.password)

            const SFID_ACCOUNT_VERIFICATION_ENABLED = getSiteEnvVarValue(config.app, 'SFID_ACCOUNT_VERIFICATION_ENABLED', siteId)

            if (SFID_ACCOUNT_VERIFICATION_ENABLED === 'true') {
                sendSignUpEventVerification()
                if (onAccountVerificationRequired) {
                    onAccountVerificationRequired(data, registrationData)
                }
            } else {
                await login.mutateAsync({
                    credentials: {
                        username: loginData.username,
                        password: loginData.password
                    },
                    body: loginData.body
                })

                window.sessionStorage.setItem('from-registration', true)
                navigate('/account')
            }
        } catch (error) {
            if (error.message === 'ACCOUNT_NOT_VERIFIED') {
                if (onAccountVerificationRequired) {
                    onAccountVerificationRequired(data)
                }
            }

            let errorMessage
            if (error.message.includes('Failed to process registration')) {
                errorMessage = formatMessage(API_ERROR_MESSAGE)
            } else if (error.message.includes('Failed to process login')) {
                errorMessage =
                    'Registration completed but login failed. Please try signing in manually.'
            } else if (error.message.includes('Invalid login data')) {
                errorMessage =
                    'Registration completed but authentication data is invalid. Please contact support.'
            } else {
                errorMessage = formatMessage(API_ERROR_MESSAGE)
            }

            form.setError('global', {
                type: 'manual',
                message: errorMessage
            })
            // GTM Modification
            sendViewErrorEvent(errorMessage)
        }
    }

    return {
        submitForm,
        isLoading: register.isLoading
    }
}

export {useRegistrationSubmit}
